import { Canvas, useFrame } from "@react-three/fiber"
import { Float, Lightformer, Text, Text3D, Center, Html, ContactShadows, Environment, MeshTransmissionMaterial, useGLTF } from "@react-three/drei"
import { Bloom, EffectComposer, N8AO, TiltShift2 } from "@react-three/postprocessing"
import * as THREE from "three"
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js"
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js"
import { Route, useLocation } from "wouter"
import { suspend } from "suspend-react"
import { easing } from "maath"
import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, Component, Suspense } from "react"
import { AnimatePresence } from "framer-motion"

// Detect mobile/low-power devices that can't handle the WebGL scene
function checkIsMobile() {
  return (
    window.innerWidth < 768 ||
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  )
}

// Error boundary so WebGL crashes don't take down the whole app
class CanvasErrorBoundary extends Component {
  state = { crashed: false }
  static getDerivedStateFromError() { return { crashed: true } }
  render() {
    if (this.state.crashed) return <div style={{ position: "fixed", inset: 0, background: "var(--bg, #f0f0f0)" }} />
    return this.props.children
  }
}
import PixelGrid from "./components/PixelGrid"
import Nav from "./components/Nav"
import BottomBlurVeil from "./components/BottomBlurVeil"
import About from "./pages/About"
import Work from "./pages/Work"
import Leadership from "./pages/Leadership"
import Writing from "./pages/Writing"
import Contact from "./pages/Contact"
import Podcast from "./pages/Podcast"
import OtherWorks from "./pages/OtherWorks"
import SwiftWorks from "./pages/SwiftWorks"
import CaseStudy from "./pages/CaseStudy"
import Skill from "./pages/Skill"
import AILab from "./pages/AILab"
import SeeYouMore from "./pages/SeeYouMore"
import LaunchedProduct from "./pages/LaunchedProduct"
import HomeTest from "./pages/HomeTest"
import LoadingScreen from "./components/LoadingScreen"

const inter = import("@pmndrs/assets/fonts/inter_regular.woff")
const interBold = import("@pmndrs/assets/fonts/inter_bold.woff")
useGLTF.preload("/bomb-gp.glb")

// MeshPhysicalMaterial defaults roughness=1 → transmission looks frosted. PMNDRS glass demos use roughness 0 + explicit ior/transmission.
const glassTorusProps = {
  backside: true,
  backsideThickness: 2.6,
  thickness: 0.95,
  roughness: 0,
  metalness: 0,
  transmission: 1,
  ior: 1.52,
  chromaticAberration: 0.03,
  anisotropicBlur: 0.015,
  samples: 14,
  // Brighter HDRI reflections — without this, glass can read as “black” on the shadow side of a single spot light.
  envMapIntensity: 1.35,
}

// Match the “clearer” transmission look used by <Bomb />.
const glassTorusLikeBombProps = {
  backsideThickness: 10,
  thickness: 5,
}

// /test: do not use transmissionSampler — it skips drei’s buffer refraction and the mesh looks opaque.
const glassTestCanvasProps = {
  thickness: 0.9,
  roughness: 0,
  metalness: 0,
  transmission: 1,
  // Stronger refraction + softer edge breakup
  ior: 1.38,
  chromaticAberration: 0.012,
  anisotropicBlur: 0.11,
  samples: 14,
  envMapIntensity: 0.22,
  attenuationColor: "#ffffff",
  attenuationDistance: 1000,
}

/** When dragging the glass torus, pause pointer-driven camera parallax so rotation feels intentional. */
const rigPauseRef = { current: false }

function Rig() {
  useFrame((state, delta) => {
    if (rigPauseRef.current) {
      state.camera.lookAt(0, 0, 0)
      return
    }
    easing.damp3(
      state.camera.position,
      [Math.sin(-state.pointer.x) * 9, state.pointer.y * 5, 15 + Math.cos(state.pointer.x) * 14],
      0.2,
      delta,
    )
    state.camera.lookAt(0, 0, 0)
  })
}


// ─── Desktop shapes (expensive MeshTransmissionMaterial) ───────────────────
const Torus = (props) => (
  <mesh receiveShadow castShadow {...props}>
    <torusGeometry args={[6.5, 1.82, 128, 64]} />
    <MeshTransmissionMaterial {...glassTorusProps} {...glassTorusLikeBombProps} />
  </mesh>
)

/** Procedural thumbs-up: rounded fist + capsule thumb, merged & centered (no external GLB). */
function createThumbUpGeometry() {
  const palm = new RoundedBoxGeometry(2.5, 2.35, 1.05, 5, 0.38).toNonIndexed()
  palm.translate(0, -0.35, 0.15)
  const thumb = new THREE.CapsuleGeometry(0.44, 1.45, 6, 18).toNonIndexed()
  thumb.rotateZ(THREE.MathUtils.degToRad(56))
  thumb.translate(-1.42, 1.05, 0.35)
  const g = mergeGeometries([palm, thumb], false)
  if (!g) {
    const fallback = new THREE.IcosahedronGeometry(6.3, 4)
    fallback.computeVertexNormals()
    return fallback
  }
  g.center()
  g.scale(2.15, 2.15, 2.15)
  g.computeVertexNormals()
  return g
}

/** Procedural "yay" gesture: torso + two raised capsule arms. */
function createYayGeometry() {
  const body = new RoundedBoxGeometry(2.1, 2.8, 1.05, 5, 0.36).toNonIndexed()
  const leftArm = new THREE.CapsuleGeometry(0.34, 2.25, 6, 18).toNonIndexed()
  const rightArm = new THREE.CapsuleGeometry(0.34, 2.25, 6, 18).toNonIndexed()

  body.translate(0, -0.65, 0)

  leftArm.rotateZ(THREE.MathUtils.degToRad(36))
  leftArm.translate(-1.7, 1.35, 0.1)

  rightArm.rotateZ(THREE.MathUtils.degToRad(-36))
  rightArm.translate(1.7, 1.35, 0.1)

  const g = mergeGeometries([body, leftArm, rightArm], false)
  if (!g) {
    const fallback = new THREE.IcosahedronGeometry(6.3, 4)
    fallback.computeVertexNormals()
    return fallback
  }
  g.center()
  g.scale(2.05, 2.05, 2.05)
  g.computeVertexNormals()
  return g
}

function WobblySphereGeometry({ radius = 7.05, widthSegments = 96, heightSegments = 96 }) {
  const geoRef = useRef(null)
  const base = useRef(null)

  useLayoutEffect(() => {
    const g = geoRef.current
    if (!g) return
    const pos = g.attributes.position
    base.current = new Float32Array(pos.array)
  }, [radius, widthSegments, heightSegments])

  useFrame((state) => {
    const g = geoRef.current
    const b = base.current
    if (!g || !b) return
    const t = state.clock.elapsedTime
    const pos = g.attributes.position
    const arr = pos.array
    for (let i = 0; i < arr.length; i += 3) {
      const bx = b[i]
      const by = b[i + 1]
      const bz = b[i + 2]
      const len = Math.hypot(bx, by, bz) || 1
      const nx = bx / len
      const ny = by / len
      const nz = bz / len
      const wobble =
        0.24 * Math.sin(nx * 3.1 + t * 2.05) * Math.cos(ny * 2.7 - t * 1.55) * Math.sin(nz * 2.5 + t * 1.15) +
        0.09 * Math.sin(t * 2.5 + nx * 5.5 + ny * 3)
      arr[i] = nx * (len + wobble)
      arr[i + 1] = ny * (len + wobble)
      arr[i + 2] = nz * (len + wobble)
    }
    pos.needsUpdate = true
    g.computeVertexNormals()
  })

  return <sphereGeometry ref={geoRef} args={[radius, widthSegments, heightSegments]} />
}

function createCameraGeometry() {
  const body = new RoundedBoxGeometry(4.8, 2.9, 1.6, 5, 0.35).toNonIndexed()
  const lensOuter = new THREE.CylinderGeometry(1.1, 1.1, 1.55, 56).toNonIndexed()
  const lensInner = new THREE.CylinderGeometry(0.68, 0.68, 1.72, 56).toNonIndexed()
  const topBump = new RoundedBoxGeometry(1.6, 0.7, 1.0, 4, 0.2).toNonIndexed()
  const sideGrip = new RoundedBoxGeometry(0.9, 1.6, 1.25, 4, 0.22).toNonIndexed()

  lensOuter.rotateX(Math.PI / 2)
  lensOuter.translate(0, 0, 1.12)
  lensInner.rotateX(Math.PI / 2)
  lensInner.translate(0, 0, 1.28)
  topBump.translate(1.2, 1.45, 0.18)
  sideGrip.translate(-2.28, -0.2, 0.08)

  const g = mergeGeometries([body, lensOuter, lensInner, topBump, sideGrip], false)
  if (!g) {
    const fallback = new THREE.IcosahedronGeometry(6.3, 4)
    fallback.computeVertexNormals()
    return fallback
  }

  g.center()
  g.scale(2.35, 2.35, 2.35)
  g.computeVertexNormals()
  return g
}

function ThumbUpBufferGeometry() {
  const geo = useMemo(() => createThumbUpGeometry(), [])
  return <primitive object={geo} attach="geometry" />
}

function YayBufferGeometry() {
  const geo = useMemo(() => createYayGeometry(), [])
  return <primitive object={geo} attach="geometry" />
}

function CameraBufferGeometry() {
  const geo = useMemo(() => createCameraGeometry(), [])
  return <primitive object={geo} attach="geometry" />
}

function HomeShapeGeometry({ shape = "torus" }) {
  if (shape === "knot") return <torusKnotGeometry args={[4.7, 1.58, 256, 48]} />
  if (shape === "sphere") return <WobblySphereGeometry />
  if (shape === "camera") return <CameraBufferGeometry />
  return <torusGeometry args={[6.5, 1.82, 128, 64]} />
}

function HomeTorus({ onHover, onUnhover, shape = "torus", onCycleShape, monoColor = "#ffffff" }) {
  const ref = useRef()
  const hovered = useRef(false)
  const dragging = useRef(false)
  const lastPointer = useRef({ x: 0, y: 0 })
  const spinAccumulator = useRef(0)
  const [activeShape, setActiveShape] = useState(shape)
  const pendingShapeRef = useRef(shape)
  const transitionPhase = useRef("idle")
  const shapeBlend = useRef(1)

  useFrame((_, delta) => {
    if (!ref.current) return
    if (transitionPhase.current === "shrinking") {
      shapeBlend.current = Math.max(0.72, shapeBlend.current - delta * 4.8)
      if (shapeBlend.current <= 0.72) {
        setActiveShape(pendingShapeRef.current)
        transitionPhase.current = "expanding"
      }
    } else if (transitionPhase.current === "expanding") {
      shapeBlend.current = Math.min(1, shapeBlend.current + delta * 4.8)
      if (shapeBlend.current >= 1) transitionPhase.current = "idle"
    }
    const target = hovered.current ? 1.2 : 1
    const s = target * shapeBlend.current
    easing.damp3(ref.current.scale, [s, s, s], 0.22, delta)
  })

  useEffect(() => {
    if (shape === activeShape) return
    pendingShapeRef.current = shape
    transitionPhase.current = "shrinking"
  }, [shape, activeShape])

  const endDrag = (e) => {
    if (!dragging.current) return
    dragging.current = false
    rigPauseRef.current = false
    try {
      e?.target?.releasePointerCapture?.(e.pointerId)
    } catch (_) {
      /* ignore */
    }
  }

  return (
    <mesh
      ref={ref}
      receiveShadow
      castShadow
      onPointerOver={(e) => { e.stopPropagation(); hovered.current = true; onHover() }}
      onPointerOut={() => { hovered.current = false; onUnhover() }}
      onPointerDown={(e) => {
        e.stopPropagation()
        dragging.current = true
        rigPauseRef.current = true
        lastPointer.current.x = e.clientX
        lastPointer.current.y = e.clientY
        e.target.setPointerCapture(e.pointerId)
      }}
      onPointerMove={(e) => {
        if (!dragging.current || !ref.current) return
        const dx = e.clientX - lastPointer.current.x
        const dy = e.clientY - lastPointer.current.y
        lastPointer.current.x = e.clientX
        lastPointer.current.y = e.clientY
        const dYaw = dx * 0.012
        ref.current.rotation.y += dYaw
        ref.current.rotation.x += dy * 0.012
        const maxTilt = Math.PI * 0.45
        ref.current.rotation.x = Math.max(-maxTilt, Math.min(maxTilt, ref.current.rotation.x))
        spinAccumulator.current += Math.abs(dYaw)
        if (spinAccumulator.current >= Math.PI * 2) {
          spinAccumulator.current -= Math.PI * 2
          onCycleShape?.()
        }
      }}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={endDrag}
    >
      <HomeShapeGeometry shape={activeShape} />
      <MeshTransmissionMaterial {...glassTorusProps} {...glassTorusLikeBombProps} color={monoColor} attenuationColor={monoColor} />
    </mesh>
  )
}

const Knot = (props) => (
  <mesh receiveShadow castShadow {...props}>
    <torusKnotGeometry args={[3, 1, 256, 32]} />
    <MeshTransmissionMaterial {...glassTorusProps} />
  </mesh>
)

function Bomb(props) {
  const { nodes } = useGLTF("/bomb-gp.glb")
  return (
    <mesh receiveShadow castShadow geometry={nodes.Little_Boy_Little_Boy_Material_0.geometry} {...props}>
      <MeshTransmissionMaterial {...glassTorusProps} backsideThickness={10} thickness={5} />
    </mesh>
  )
}

// ─── Test page: torus, rig, status matching reference code ─────────────────
function TestTorus(props) {
  return (
    <mesh receiveShadow castShadow {...props}>
      <torusGeometry args={[5.2, 1.56, 128, 64]} />
      <MeshTransmissionMaterial {...glassTorusProps} {...glassTorusLikeBombProps} />
    </mesh>
  )
}

function TestTorusAuto({ shape = "torus", onCycleShape, monoColor = "#ffffff", onHover, onUnhover, ...props }) {
  const ref = useRef()
  const hovered = useRef(false)
  const hoverScale = useRef(1)
  const spinAccumulator = useRef(0)
  const [activeShape, setActiveShape] = useState(shape)
  const pendingShapeRef = useRef(shape)
  const transitionPhase = useRef("idle")
  const shapeBlend = useRef(1)

  useFrame((_, delta) => {
    if (!ref.current) return
    if (transitionPhase.current === "shrinking") {
      shapeBlend.current = Math.max(0.72, shapeBlend.current - delta * 4.2)
      if (shapeBlend.current <= 0.72) {
        setActiveShape(pendingShapeRef.current)
        transitionPhase.current = "expanding"
      }
    } else if (transitionPhase.current === "expanding") {
      shapeBlend.current = Math.min(1, shapeBlend.current + delta * 4.2)
      if (shapeBlend.current >= 1) transitionPhase.current = "idle"
    }
    const dYaw = delta * 0.45
    ref.current.rotation.y += dYaw
    ref.current.rotation.x += delta * 0.08
    const targetHover = hovered.current ? 1.2 : 1
    hoverScale.current += (targetHover - hoverScale.current) * Math.min(1, 14 * delta)
    const s = shapeBlend.current * hoverScale.current
    ref.current.scale.set(s, s, s)
    spinAccumulator.current += Math.abs(dYaw)
    if (spinAccumulator.current >= Math.PI * 2) {
      spinAccumulator.current -= Math.PI * 2
      onCycleShape?.()
    }
  })

  useEffect(() => {
    if (shape === activeShape) return
    pendingShapeRef.current = shape
    transitionPhase.current = "shrinking"
  }, [shape, activeShape])

  return (
    <mesh
      ref={ref}
      receiveShadow
      castShadow
      {...props}
      onPointerOver={(e) => {
        e.stopPropagation()
        hovered.current = true
        onHover?.()
      }}
      onPointerOut={() => {
        hovered.current = false
        onUnhover?.()
      }}
    >
      <HomeShapeGeometry shape={activeShape} />
      <MeshTransmissionMaterial {...glassTestCanvasProps} color={monoColor} attenuationColor={monoColor} />
    </mesh>
  )
}

function TestRig() {
  useFrame((state, delta) => {
    easing.damp3(
      state.camera.position,
      [Math.sin(-state.pointer.x) * 5, state.pointer.y * 3.5, 15 + Math.cos(state.pointer.x) * 10],
      0.2,
      delta,
    )
    state.camera.lookAt(0, 0, 0)
  })
}

function TestStatus(props) {
  return (
    <Text fontSize={14} letterSpacing={-0.025} font={suspend(interBold).default} color="black" {...props}>
      /RICE
      <Html style={{ color: "transparent", fontSize: "33.5em", pointerEvents: "none" }} transform>
        /RICE
      </Html>
    </Text>
  )
}

// ─── Glass text for /test page ─────────────────────────────────────────────
function GlassText({ onHover, onUnhover }) {
  const groupRef = useRef()
  const hovered = useRef(false)

  useFrame((_, delta) => {
    if (!groupRef.current) return
    const target = hovered.current ? 1.32 : 1
    easing.damp3(groupRef.current.scale, [target, target, target], 0.22, delta)
  })

  return (
    <group ref={groupRef}>
      <Center>
        <Text3D
          font="/pacifico.json"
          size={8}
          height={2.5}
          curveSegments={48}
          bevelEnabled
          bevelThickness={0.5}
          bevelSize={0.18}
          bevelSegments={12}
          onPointerOver={(e) => { e.stopPropagation(); hovered.current = true; onHover?.() }}
          onPointerOut={() => { hovered.current = false; onUnhover?.() }}
        >
          RICE
          <MeshTransmissionMaterial
            {...glassTorusProps}
            backsideThickness={6}
            thickness={3}
            chromaticAberration={0.08}
            anisotropy={0.4}
            anisotropicBlur={0.4}
          />
        </Text3D>
      </Center>
    </group>
  )
}

// ─── Mobile shapes (lightweight meshStandardMaterial) ──────────────────────
const mobileMat = <meshStandardMaterial metalness={1} roughness={0.08} color="#c0c0c0" />

const MobileTorus = ({ onHover, onUnhover, ...props }) => (
  <mesh
    {...props}
    onPointerOver={onHover ? (e) => { e.stopPropagation(); onHover() } : undefined}
    onPointerOut={onUnhover}
  >
    <torusGeometry args={[6.5, 1.82, 64, 32]} />
    {mobileMat}
  </mesh>
)

const MobileKnot = (props) => (
  <mesh {...props}>
    <torusKnotGeometry args={[3, 1, 128, 16]} />
    {mobileMat}
  </mesh>
)


function ParticleCloud({ color = "#888888", count = 5000, exploded = false }) {
  const pointsRef = useRef()
  const geoRef    = useRef()
  const matRef    = useRef()

  // ── home: gaussian cloud tight around the torus ──────────────────────────
  const homePositions = useMemo(() => {
    const arr = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      const u = Math.max(1e-6, Math.random())
      const v = Math.random()
      const mag = Math.sqrt(-2 * Math.log(u))
      arr[i * 3]     = mag * Math.cos(2 * Math.PI * v) * 4.5
      arr[i * 3 + 1] = mag * Math.sin(2 * Math.PI * v) * 6.5
      arr[i * 3 + 2] = (Math.random() - 0.5) * 4
    }
    return arr
  }, [count])

  // ── exploded: spread across the full viewport sphere ─────────────────────
  const explodedPositions = useMemo(() => {
    const arr = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2
      const phi   = Math.acos(2 * Math.random() - 1)
      const r     = 10 + Math.random() * 22           // radius 10–32
      arr[i * 3]     = Math.sin(phi) * Math.cos(theta) * r
      arr[i * 3 + 1] = Math.sin(phi) * Math.sin(theta) * r
      arr[i * 3 + 2] = Math.cos(phi) * r * 0.35       // flatten z so they stay on-screen
    }
    return arr
  }, [count])

  // ── live buffer (starts at home, gets lerped every frame) ─────────────────
  const livePositions = useMemo(() => homePositions.slice(), [homePositions])

  useFrame((state, delta) => {
    if (!pointsRef.current || !geoRef.current || !matRef.current) return
    const t = state.clock.elapsedTime

    const target     = exploded ? explodedPositions : homePositions
    const lerpSpeed  = exploded ? 7 : 2.8           // explode fast, drift back slowly

    for (let i = 0; i < count * 3; i++) {
      livePositions[i] += (target[i] - livePositions[i]) * Math.min(1, lerpSpeed * delta)
    }
    geoRef.current.attributes.position.array.set(livePositions)
    geoRef.current.attributes.position.needsUpdate = true

    // Opacity & size: brighter + bigger when exploded so they're clearly visible
    const targetOpacity = exploded ? 0.75 : 0.9
    const targetSize    = exploded ? 0.12 : 0.08
    matRef.current.opacity += (targetOpacity - matRef.current.opacity) * Math.min(1, 5 * delta)
    matRef.current.size    += (targetSize    - matRef.current.size)    * Math.min(1, 5 * delta)

    // Orbital rotation: keep spinning when home, slow-freeze when exploded
    if (!exploded) {
      pointsRef.current.rotation.z += delta * 0.08
      pointsRef.current.rotation.y += delta * 0.035
      pointsRef.current.rotation.x  = Math.sin(t * 0.15) * 0.25
    } else {
      // gently drift to near-zero tilt while exploded
      pointsRef.current.rotation.x += (0 - pointsRef.current.rotation.x) * delta * 2
    }
  })

  return (
    <points ref={pointsRef} position={[0, 0, -6]}>
      <bufferGeometry ref={geoRef}>
        <bufferAttribute attach="attributes-position" count={count} array={livePositions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial ref={matRef} size={0.08} color={color} transparent opacity={0.9} sizeAttenuation depthWrite={false} />
    </points>
  )
}

// ─── Dedicated test canvas — exact replica of reference code ───────────────
function TestCanvas({
  monoColor = "#ffffff",
  isLight = true,
  interactive = true,
  isMobile = false,
  eventSourceEl = null,
  onTorusHover,
  onTorusLeave,
}) {
  const testShapes = ["torus", "knot", "sphere", "camera"]
  const [testShapeIndex, setTestShapeIndex] = useState(0)
  const testShape = testShapes[testShapeIndex]
  const riceFontSize = isMobile ? 9 : 14
  // Mobile: the Html overlay text is very large; reduce to avoid cropping/strange zoom.
  const riceHtmlFontSize = isMobile ? "18em" : "33.5em"

  return (
    <Canvas
      eventSource={interactive && eventSourceEl ? eventSourceEl : undefined}
      eventPrefix="client"
      shadows
      dpr={isMobile ? [1, 1.25] : [1, 2]}
      gl={isMobile ? { antialias: false, powerPreference: "low-power", alpha: false, stencil: false, depth: true } : undefined}
      camera={{ position: [0, 0, 20], fov: 50 }}
      style={{ position: "fixed", inset: 0, pointerEvents: interactive ? "auto" : "none" }}
    >
      <color attach="background" args={[isLight ? "#e3e3e3" : "#121417"]} />
      {/* Mobile: flatter hemisphere + no contact shadow — narrow viewports make the shadow plane read as a solid gray band */}
      <hemisphereLight
        args={
          isLight
            ? isMobile
              ? ["#ececec", "#e6e6e6", 0.42]
              : ["#f3f3f3", "#b6b6b6", 0.36]
            : ["#969da8", "#171b21", 0.5]
        }
      />
      <spotLight position={[20, 20, 10]} penumbra={1} castShadow angle={0.2} intensity={isLight ? 1.05 : 1.4} />
      <Suspense fallback={null}>
        <Text fontSize={riceFontSize} letterSpacing={-0.025} font={suspend(inter).default} color={isLight ? "black" : "#d9dde5"} position={[0, -0.5, -10]}>
          /RICE
          <Html style={{ color: "transparent", fontSize: riceHtmlFontSize, pointerEvents: "none" }} transform>
            /RICE
          </Html>
        </Text>
      </Suspense>
      <Float floatIntensity={2}>
        <TestTorusAuto
          monoColor={monoColor}
          shape={testShape}
          onCycleShape={() => setTestShapeIndex((i) => (i + 1) % testShapes.length)}
          onHover={onTorusHover}
          onUnhover={onTorusLeave}
        />
      </Float>
      {/* Same route as /test — keep post FX aligned with desktop “test” tuning so localhost:/test visibly updates */}
      <ambientLight intensity={0.35} />
      {!isMobile && <ContactShadows scale={100} position={[0, -7.5, 0]} blur={1} far={100} opacity={0.38} />}
      <Environment environmentIntensity={0.4}>
        <Lightformer intensity={2.2} color="#ffffff" position={[10, 5, 0]} scale={[10, 50, 1]} onUpdate={(self) => self.lookAt(0, 0, 0)} />
        <Lightformer intensity={1.2} color="#ffffff" position={[-8, -2, 4]} scale={[6, 12, 1]} onUpdate={(self) => self.lookAt(0, 0, 0)} />
      </Environment>
      {/* Skip post-processing on mobile — extra render targets often push iOS WebGL over memory limits */}
      {!isMobile && (
        <EffectComposer disableNormalPass>
          <Bloom mipmapBlur luminanceThreshold={0.9} intensity={0.32} levels={8} />
          <TiltShift2 blur={0.2} />
        </EffectComposer>
      )}
      <TestRig />
    </Canvas>
  )
}

function SceneBackground({ color = "#282828", fontSize = 14, text = "RICE TSENG", bold = false, ...props }) {
  const font = bold ? suspend(interBold).default : suspend(inter).default
  return (
    <Text fontSize={fontSize} letterSpacing={-0.04} font={font} color={color} {...props}>
      {text}
    </Text>
  )
}

export const App = () => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [torusHovered, setTorusHovered] = useState(false)
  const [homeShapeIndex, setHomeShapeIndex] = useState(3)
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? checkIsMobile() : false))
  const [initialLoading, setInitialLoading] = useState(true)
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light")
  const [loc] = useLocation()
  const isHome = loc === "/"
  const isSkill = loc === "/skill"
  // Work detail pages (/case/okx, /case/defi, /case/grab). Matched by
  // prefix rather than listed one by one so a new case study doesn't
  // silently get the veil back.
  const isCaseStudy = loc.startsWith("/case/")

  // SPA: browser does not reset scroll on pushState — prevents content “jumping” after route changes.
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }, [loc])
  // Treat homepage as "test mode" too, so the / canvas matches the confirmed /test version.
  const isTestHome = loc === "/test" || loc === "/"
  const shouldUseTestCanvas = isTestHome
  const isLight = theme === "light"
  const canvasWrapperRef = useRef(null)
  const testCanvasWrapperRef = useRef(null)
  const [testCanvasEventSource, setTestCanvasEventSource] = useState(null)
  const [mainCanvasEventSource, setMainCanvasEventSource] = useState(null)

  const setTestCanvasWrapperEl = useCallback((el) => {
    testCanvasWrapperRef.current = el
    setTestCanvasEventSource(el)
  }, [])

  const setCanvasWrapperEl = useCallback((el) => {
    canvasWrapperRef.current = el
    setMainCanvasEventSource(el)
  }, [])
  const homeShapes = ["torus", "knot", "sphere", "camera"]
  const homeShape = homeShapes[homeShapeIndex]
  // Keep shape bright in dark mode; only background/text should invert.
  const monoColor = "#ffffff"

  useEffect(() => {
    setIsMobile(checkIsMobile())
  }, [])

  useEffect(() => {
    document.body.dataset.theme = theme
    localStorage.setItem("theme", theme)
  }, [theme])

  useEffect(() => {
    if (menuOpen) setTorusHovered(false)
  }, [menuOpen])

  const toggleTheme = () => setTheme(t => t === "dark" ? "light" : "dark")
  const showHomeLoading = isHome && initialLoading

  // Use HTML `inert` attribute — completely removes element from all events,
  // more reliable than CSS pointer-events alone
  useEffect(() => {
    const shouldInert = !isHome || menuOpen
    for (const ref of [canvasWrapperRef, testCanvasWrapperRef]) {
      const el = ref.current
      if (!el) continue
      if (shouldInert) el.setAttribute("inert", "")
      else el.removeAttribute("inert")
    }
  }, [isHome, menuOpen])

  return (
    <>
      <AnimatePresence mode="wait">
        {showHomeLoading && <LoadingScreen onComplete={() => setInitialLoading(false)} />}
      </AnimatePresence>

      <main style={{ opacity: showHomeLoading ? 0 : 1, transition: "opacity 0.5s ease-out" }}>
        {/* Only one WebGL canvas at a time — avoids two contexts on home (mobile Safari OOM / tab crash). */}
        {shouldUseTestCanvas && (
          <div
            ref={setTestCanvasWrapperEl}
            style={{ position: "fixed", inset: 0, zIndex: 0 }}
          >
            <CanvasErrorBoundary>
              <TestCanvas
                monoColor={monoColor}
                isLight={isLight}
                interactive={!menuOpen}
                isMobile={isMobile}
                eventSourceEl={testCanvasEventSource}
                onTorusHover={() => setTorusHovered(true)}
                onTorusLeave={() => setTorusHovered(false)}
              />
            </CanvasErrorBoundary>
          </div>
        )}

        {!shouldUseTestCanvas && (
        <div
          ref={setCanvasWrapperEl}
          style={{ position: "fixed", inset: 0, zIndex: 0 }}
        >
        <CanvasErrorBoundary>
        {isMobile ? (
          /* ── Mobile: lightweight meshes + no heavy post — keeps iOS GPU memory stable ── */
          <Canvas
            frameloop={isHome ? "always" : "never"}
            camera={{ position: [0, 0, 20], fov: 50 }}
            dpr={[1, 1.25]}
            gl={{ antialias: false, powerPreference: "low-power", alpha: false, stencil: false, depth: true }}
            fallback={null}
          >
            <color attach="background" args={[isLight ? "#f0f0f0" : "#111111"]} />
            <ambientLight intensity={0.3} />
            <spotLight position={[20, 20, 10]} penumbra={1} angle={0.2} intensity={12} castShadow={false} />
            <spotLight position={[-10, -10, 10]} penumbra={1} angle={0.3} intensity={4} />
            <pointLight position={[0, 8, 6]} intensity={30} color="#ffffff" />
            {!isTestHome && <Suspense fallback={null}><SceneBackground position={[0, 0, -10]} color={isLight ? "#bbbbbb" : "#484848"} fontSize={10} text="RICE" /></Suspense>}
            {isTestHome && <Suspense fallback={null}><SceneBackground position={[0, 0, -10]} color={isLight ? "#aaaaaa" : "#555555"} fontSize={12} text="/RICE" bold /></Suspense>}
            {isHome && !isTestHome && <ParticleCloud color={isLight ? "#666666" : "#999999"} count={2500} exploded={torusHovered} />}
            {isTestHome && <ContactShadows scale={60} position={[0, -8, 0]} blur={4} far={20} opacity={isLight ? 0.2 : 0.35} />}
            <Float floatIntensity={2}>
              <Route path="/test"><MobileTorus onHover={() => setTorusHovered(true)} onUnhover={() => setTorusHovered(false)} /></Route>
              <Route path="/"><MobileTorus onHover={() => setTorusHovered(true)} onUnhover={() => setTorusHovered(false)} /></Route>
              <Route path="/about"><MobileKnot /></Route>
              <Route path="/about/"><MobileKnot /></Route>
              <Route path="/work"><MobileTorus /></Route>
              <Route path="/work/"><MobileTorus /></Route>
              <Route path="/leadership"><MobileKnot /></Route>
              <Route path="/leadership/"><MobileKnot /></Route>
              <Route path="/writing"><MobileTorus /></Route>
              <Route path="/writing/"><MobileTorus /></Route>
              <Route path="/podcast"><MobileKnot /></Route>
              <Route path="/podcast/"><MobileKnot /></Route>
              <Route path="/contact"><MobileTorus /></Route>
              <Route path="/contact/"><MobileTorus /></Route>
            </Float>
            <Environment files="/taipei_mobile.jpg">
              <Lightformer intensity={6} position={[10, 5, 0]} scale={[10, 50, 1]} onUpdate={(self) => self.lookAt(0, 0, 0)} />
            </Environment>
            <Rig />
          </Canvas>
        ) : (
          /* ── Desktop: full quality scene ── */
          <Canvas
            {...(isHome && mainCanvasEventSource ? { eventSource: mainCanvasEventSource, eventPrefix: "client" } : {})}
            frameloop={isHome ? "always" : "never"}
            shadows
            camera={{ position: [0, 0, 20], fov: 50 }}
            fallback={null}
          >
            <color attach="background" args={[isTestHome ? "#e0e0e0" : (isLight ? "#f0f0f0" : "#111111")]} />
            <spotLight position={[20, 20, 10]} penumbra={1} castShadow angle={0.2} />
            {isTestHome && <ambientLight intensity={0.35} />}
            {!isTestHome && <Suspense fallback={null}><SceneBackground position={[0, 0, -10]} color={isLight ? "#aaaaaa" : "#282828"} fontSize={isMobile ? 10 : 14} /></Suspense>}
            {isTestHome && <Suspense fallback={null}><TestStatus position={[0, 0, -10]} /></Suspense>}
            {isHome && !isTestHome && <ParticleCloud color={isLight ? "#777777" : "#888888"} count={5000} exploded={torusHovered} />}
            {isTestHome && <ContactShadows scale={100} position={[0, -7.5, 0]} blur={1} far={100} opacity={0.5} />}
            <Float floatIntensity={2}>
              <Route path="/test"><TestTorus /></Route>
              <Route path="/"><HomeTorus monoColor={monoColor} shape={homeShape} onCycleShape={() => setHomeShapeIndex((i) => (i + 1) % homeShapes.length)} onHover={() => setTorusHovered(true)} onUnhover={() => setTorusHovered(false)} /></Route>
              <Route path="/about"><Knot /></Route>
              <Route path="/about/"><Knot /></Route>
              <Route path="/work"><Bomb scale={0.7} /></Route>
              <Route path="/work/"><Bomb scale={0.7} /></Route>
              <Route path="/leadership"><Knot /></Route>
              <Route path="/leadership/"><Knot /></Route>
              <Route path="/writing"><Torus /></Route>
              <Route path="/writing/"><Torus /></Route>
              <Route path="/podcast"><Knot /></Route>
              <Route path="/podcast/"><Knot /></Route>
              <Route path="/contact"><Torus /></Route>
              <Route path="/contact/"><Torus /></Route>
            </Float>
            {!isTestHome && <ContactShadows scale={100} position={[0, -60, 0]} blur={3} far={100} opacity={isLight ? 0.06 : 0.2} />}
            {!isTestHome ? (
              <Environment files="/taipei_panoramic photo.jpg">
                <Lightformer intensity={8} position={[10, 5, 0]} scale={[10, 50, 1]} onUpdate={(self) => self.lookAt(0, 0, 0)} />
              </Environment>
            ) : (
              <Environment preset="city">
                <Lightformer intensity={8} position={[10, 5, 0]} scale={[10, 50, 1]} onUpdate={(self) => self.lookAt(0, 0, 0)} />
              </Environment>
            )}
            <EffectComposer disableNormalPass>
              {/* N8AO darkens creases; on transmission glass it often reads as “dirty / black” — ease off on /test */}
              <N8AO aoRadius={1} intensity={isTestHome ? 0.35 : 1.1} />
              {!isTestHome
                ? <Bloom mipmapBlur luminanceThreshold={0.9} intensity={0.95} levels={8} />
                : <Bloom mipmapBlur luminanceThreshold={0.8} intensity={1.6} levels={8} />}
              <TiltShift2 blur={0.14} />
            </EffectComposer>
            {isTestHome ? <TestRig /> : <Rig />}
          </Canvas>
        )}
        </CanvasErrorBoundary>
        </div>
        )}

        <Nav menuOpen={menuOpen} setMenuOpen={setMenuOpen} isHome={isHome} isSkill={isSkill} theme={theme} toggleTheme={toggleTheme} />

        {isTestHome && (
          <div className={`torus-labels${torusHovered ? " visible" : ""}${isLight ? " torus-labels--light" : ""}`}>
            <span className="torus-label torus-label-taiwan">Taiwan</span>
            <span className="torus-label torus-label-singapore">Singapore</span>
          </div>
        )}

        {/* Re-keyed on every navigation — forces re-mount → re-triggers CSS animation */}
        <div key={loc} className="page-transition-overlay" />

        {/* Global bottom blur veil — applies to every .page-panel sub-page
            (About, Work, Leadership, etc). Skipped on the home/test canvas
            scene, while the nav menu overlay is open (neither is a
            scrolling content panel), on /launched and /ai-lab (both use
            the same fixed-viewport carousel layout, no scroll), on
            /skill, and on the /case/:id work detail pages. */}
        {!isTestHome && !menuOpen && loc !== "/launched" && loc !== "/ai-lab" && !isSkill && !isCaseStudy && <BottomBlurVeil />}

        <Route path="/"><HomeTest /></Route>
        <Route path="/test"><HomeTest /></Route>
        <Route path="/about"><About /></Route>
        <Route path="/about/"><About /></Route>
        <Route path="/work"><Work /></Route>
        <Route path="/work/"><Work /></Route>
        <Route path="/leadership"><Leadership /></Route>
        <Route path="/leadership/"><Leadership /></Route>
        <Route path="/writing"><Writing /></Route>
        <Route path="/writing/"><Writing /></Route>
        <Route path="/podcast"><Podcast /></Route>
        <Route path="/podcast/"><Podcast /></Route>
        <Route path="/other-works"><OtherWorks /></Route>
        <Route path="/swift"><SwiftWorks /></Route>
        <Route path="/case/:id">{(params) => <CaseStudy id={params.id} />}</Route>
        <Route path="/skill"><Skill /></Route>
        <Route path="/ai-lab"><AILab /></Route>
        <Route path="/seeyoumore"><SeeYouMore /></Route>
        <Route path="/launched"><LaunchedProduct /></Route>
        <Route path="/contact"><Contact /></Route>
        <Route path="/contact/"><Contact /></Route>
      </main>
    </>
  )
}
